import { IconAlertCircle, IconCheck, IconEdit } from '@myplayer/ui';
import { type ReactElement } from 'react';
import { Badge, Button, Input, ModalDialog, Select, Switch } from './components/ui/primitives.js';
import { type GeneratedGs1QrPrintAssets } from './qr-generator.js';
import {
  QR_BACKGROUND_PRESETS,
  QR_DOT_SHAPE_OPTIONS,
  QR_FOREGROUND_PRESETS,
  QR_LOGO_SIZE_OPTIONS,
  STYLED_QR_DISCLAIMER_LABEL,
  type QrDotShape,
  type QrLogoScaleOption,
  type QrStyleConfig,
  type QrStyleRuleEvaluation,
} from './qr-style-rules.js';

export function QrRuleViolationAlert(p: {
  readonly ruleEvaluation: QrStyleRuleEvaluation;
  readonly onResetSafeDefaults?: (() => void) | undefined;
}): ReactElement | null {
  if (p.ruleEvaluation.valid || p.ruleEvaluation.primaryMessage === null) return null;
  return (
    <div
      role="alert"
      data-testid="qr-style-rule-error"
      className="flex items-start justify-between gap-3 rounded-control border border-status-error-border bg-status-error-bg p-3 text-[12px] text-status-error"
    >
      <div className="flex items-start gap-2">
        <IconAlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
        <div className="space-y-1">
          {p.ruleEvaluation.violations.map((v) => (
            <p key={v.code} className="font-medium leading-relaxed">
              {v.message}
            </p>
          ))}
        </div>
      </div>
      {p.onResetSafeDefaults !== undefined && (
        <Button
          type="button"
          size="sm"
          variant="secondary"
          data-testid="qr-reset-safe-defaults-button"
          onClick={p.onResetSafeDefaults}
        >
          Reset to safe defaults
        </Button>
      )}
    </div>
  );
}

function LabeledSelect(p: {
  readonly id: string;
  readonly label: string;
  readonly testId: string;
  readonly value: string;
  readonly options: readonly {
    readonly key: string;
    readonly value: string;
    readonly label: string;
  }[];
  readonly onChange: (val: string) => void;
}): ReactElement {
  return (
    <div className="space-y-1.5">
      <label htmlFor={p.id} className="block text-[12px] font-medium">
        {p.label}
      </label>
      <Select
        id={p.id}
        aria-label={p.label}
        data-testid={p.testId}
        value={p.value}
        onChange={(e) => p.onChange(e.target.value)}
      >
        {p.options.map((o) => (
          <option key={o.key} value={o.value}>
            {o.label}
          </option>
        ))}
      </Select>
    </div>
  );
}

export function QrStyleEditorModal(p: {
  readonly open: boolean;
  readonly cardTestId: string;
  readonly style: QrStyleConfig;
  readonly styledAssets: GeneratedGs1QrPrintAssets | null;
  readonly ruleEvaluation: QrStyleRuleEvaluation;
  readonly disableActions: boolean;
  readonly onClose: () => void;
  readonly onUpdateStyle: (patch: Partial<QrStyleConfig>) => void;
  readonly onResetSafeDefaults: () => void;
  readonly onSaveStyled: () => void;
}): ReactElement {
  const { cardTestId, style, styledAssets, ruleEvaluation, disableActions, onUpdateStyle } = p;
  const fgOpts = QR_FOREGROUND_PRESETS.map((x) => ({ key: x.id, value: x.hex, label: x.label }));
  const bgOpts = QR_BACKGROUND_PRESETS.map((x) => ({ key: x.id, value: x.hex, label: x.label }));
  const shapeOpts = QR_DOT_SHAPE_OPTIONS.map((o) => ({
    key: o.value,
    value: o.value,
    label: o.label,
  }));
  return (
    <ModalDialog
      open={p.open}
      title="Edit QR code style"
      subtitle="Customise colours, dot shape, and centre logo while keeping the QR code scannable."
      icon={<IconEdit />}
      maxWidthClassName="max-w-2xl"
      onClose={p.onClose}
      data-testid="qr-style-modal"
      footer={
        <>
          <Button
            type="button"
            size="sm"
            variant="secondary"
            data-testid="qr-reset-safe-defaults-button"
            onClick={p.onResetSafeDefaults}
          >
            Reset to safe defaults
          </Button>
          <Button type="button" size="sm" variant="secondary" onClick={p.onClose}>
            Cancel
          </Button>
          <Button
            type="button"
            size="sm"
            variant="primary"
            disabled={disableActions}
            data-testid="save-styled-qr-button"
            onClick={() => (p.onSaveStyled(), p.onClose())}
          >
            <IconCheck />
            <span>Save design</span>
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <div className="flex items-center gap-4 rounded-control border border-subtle bg-surface-subtle p-3">
          {styledAssets !== null && (
            <div
              className="h-24 w-24 shrink-0 rounded-control border border-subtle bg-white p-1.5 [&>svg]:h-full [&>svg]:w-full"
              dangerouslySetInnerHTML={{ __html: styledAssets.svg }}
            />
          )}
          <div className="min-w-0 flex-1 space-y-1 text-[12px]">
            <p className="font-semibold text-fg">Live preview</p>
            <p className="text-fg-secondary tabular-nums">
              Contrast ratio: {ruleEvaluation.contrastRatio.toFixed(1)}:1
            </p>
            <Badge variant="warning">{STYLED_QR_DISCLAIMER_LABEL}</Badge>
          </div>
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <LabeledSelect
            id={`${cardTestId}-fg-color`}
            label="Dot colour"
            testId="qr-style-fg-select"
            value={style.fgHex}
            options={fgOpts}
            onChange={(fgHex) => onUpdateStyle({ fgHex })}
          />
          <LabeledSelect
            id={`${cardTestId}-bg-color`}
            label="Background colour"
            testId="qr-style-bg-select"
            value={style.bgHex}
            options={bgOpts}
            onChange={(bgHex) => onUpdateStyle({ bgHex })}
          />
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <LabeledSelect
            id={`${cardTestId}-dot-shape`}
            label="Dot shape"
            testId="qr-style-dot-shape-select"
            value={style.dotShape}
            options={shapeOpts}
            onChange={(dotShape) => onUpdateStyle({ dotShape: dotShape as QrDotShape })}
          />
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <label htmlFor={`${cardTestId}-logo-toggle`} className="text-[12px] font-medium">
                Centre logo
              </label>
              <Switch
                id={`${cardTestId}-logo-toggle`}
                aria-label="Centre logo"
                data-testid="qr-style-logo-switch"
                checked={style.showLogo}
                onChange={(e) => onUpdateStyle({ showLogo: e.target.checked })}
              />
            </div>
            {style.showLogo ? (
              <div className="grid grid-cols-2 gap-2 pt-1">
                <Select
                  id={`${cardTestId}-logo-size`}
                  aria-label="Logo size"
                  data-testid="qr-style-logo-size-select"
                  value={String(style.logoScalePercent)}
                  onChange={(e) =>
                    onUpdateStyle({ logoScalePercent: Number(e.target.value) as QrLogoScaleOption })
                  }
                >
                  {QR_LOGO_SIZE_OPTIONS.map((o) => (
                    <option key={o.value} value={String(o.value)}>
                      {o.label}
                    </option>
                  ))}
                </Select>
                <Input
                  id={`${cardTestId}-logo-label`}
                  aria-label="Logo initials"
                  data-testid="qr-style-logo-label-input"
                  maxLength={4}
                  value={style.logoLabel}
                  onChange={(e) => onUpdateStyle({ logoLabel: e.target.value })}
                  placeholder="MP"
                />
              </div>
            ) : (
              <p className="pt-1 text-[11px] text-fg-secondary">Off by default for scanning.</p>
            )}
          </div>
        </div>
        <QrRuleViolationAlert ruleEvaluation={ruleEvaluation} />
      </div>
    </ModalDialog>
  );
}
